import { z } from 'zod';

import { paymentPreferencesSchema } from 'src/legacy/components/billing/new/types';
import {
  LineItemType,
  MAX_LINE_ITEM_AMOUNT,
  MIN_LINE_ITEM_AMOUNT,
} from 'src/services/api/invoicesApi';

const SUBSCRIPTION_ERROR = 'Please add at least one subscription';
const ONE_TIME_PRODUCT_ERROR = 'Please add at least one one-time product';

export enum ProductStoreVisibility {
  Internal = 'internal',
  ClientUser = 'client_user',
  Public = 'public',
  All = 'all',
}

export enum ProductType {
  Subscription = 'subscription',
  OneTime = 'oneTime',
}

export const productStoreLineItemSchema = z.object({
  name: z.string().optional(),
  productId: z.string().optional(),
  type: z.literal(LineItemType.Product).optional(),
  priceId: z.string().optional(),
  id: z.string().optional(),
  quantity: z.string().optional(),
  amount: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (value) {
          const number = parseFloat(value);
          return number >= MIN_LINE_ITEM_AMOUNT;
        }
        return true;
      },
      { message: 'Amount must be between 0.5 and 100,000' },
    )
    .refine(
      (value) => {
        if (value) {
          const number = parseFloat(value);
          return number <= MAX_LINE_ITEM_AMOUNT;
        }
        return true;
      },
      { message: 'Amount can be at most 100,000' },
    ),
  isEditing: z.boolean().optional(),
  isValid: z.boolean().optional(),
  shouldAddPrice: z.boolean().optional(),
  features: z
    .array(
      z
        .string()
        .max(100, { message: 'Feature must be under 100 characters' })
        .optional(),
    )
    .optional(),
  imageUrl: z.string().optional(),
  isImageUrlSetManually: z.boolean().optional(),
  description: z.string().optional(),
});

export const productStorePreferencesSchema = z.object({
  visibility: z.enum([
    ProductStoreVisibility.Internal,
    ProductStoreVisibility.Public,
    ProductStoreVisibility.ClientUser,
    ProductStoreVisibility.All,
  ]),
  showOnlyOneTimeProducts: z.boolean(),
  showOnlySubscriptions: z.boolean(),
  paymentPreferences: paymentPreferencesSchema,
  currency: z.string(),
});

export const productStoreSchema = z
  .object({
    id: z.string().optional(),
    displayName: z
      .string({
        required_error: 'Store name is required',
      })
      .max(100, { message: 'Store name must be 100 characters or less' }),
    productStorePreferences: productStorePreferencesSchema,
    subscriptions: z.array(productStoreLineItemSchema).max(4),
    oneTimeProducts: z.array(productStoreLineItemSchema).max(12),
  })
  .refine(
    (data) => {
      const { productStorePreferences, subscriptions } = data;
      const isSubscriptionEnabled =
        productStorePreferences.showOnlySubscriptions === true;

      return !(isSubscriptionEnabled && subscriptions.length === 0);
    },
    {
      message: SUBSCRIPTION_ERROR,
      path: ['subscriptions'],
    },
  )
  .refine(
    (data) => {
      const { productStorePreferences, oneTimeProducts } = data;
      const isOneTimeProductEnabled =
        productStorePreferences.showOnlyOneTimeProducts === true;

      return !(isOneTimeProductEnabled && oneTimeProducts.length === 0);
    },
    {
      message: ONE_TIME_PRODUCT_ERROR,
      path: ['oneTimeProducts'],
    },
  );

export type ProductStoreLineItem = z.infer<typeof productStoreLineItemSchema>;
export type ProductStoreSchema = z.infer<typeof productStoreSchema>;
export type ProductStorePreferencesSchema = z.infer<
  typeof productStorePreferencesSchema
>;
